import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Search, X } from 'lucide-react';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { DropdownItemModel } from '../ExitSlip/ExitSlipModalFilters';
import { AvailablesOptions, FormState } from '../../pages/SingleUnit';
import { useAppDispatch } from '../../redux/hooks';
import { setTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { setUnit } from '../../redux/reducers/unitSlice';
import { Skill } from '../../models/Skill';

interface Props {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
  optionsList: AvailablesOptions;
  availablesOptions: AvailablesOptions;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  skillSuggestions: Skill[];
  onSkillSelect: (skill: Skill) => void;
}

const UnitsReportSettings: FC<Props> = ({
  form,
  setForm,
  optionsList,
  availablesOptions,
  searchQuery,
  onSearchChange,
  skillSuggestions,
  onSkillSelect,
}) => {
  const dispatch = useAppDispatch();
  const [filteredRoadmaps, setFilteredRoadmaps] = useState<DropdownItemModel[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<DropdownItemModel[]>([]);

  const isSearching = Boolean(searchQuery.trim());

  const handleRoadmapChange = (value: DropdownItemModel) => {
    setForm(prev => ({ ...prev, roadmap: value, unitEnabled: !!value?.value, classEnabled: false }));
    dispatch(setTargetGroup(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  const handleUnitChange = (value: DropdownItemModel) => {
    setForm(prev => ({ ...prev, unit: value, classEnabled: !!value?.value }));
    dispatch(setUnit(value?.value ? { id: value.value, name: value.label } : undefined));
  };

  const filterBy = (event: AutoCompleteCompleteEvent, items: DropdownItemModel[]) =>
    !event.query.trim().length
      ? [...items]
      : items.filter(item => item.label.toLowerCase().includes(event.query.toLowerCase()));

  return (
    <div className="nc_report-settings" id="unitSelector">
      <span className="nc_report-settings__title">Report settings</span>

      <div className={`nc_report-settings__search ${isSearching ? 'nc_report-settings__search--active' : ''}`}>
        <Search size={18} aria-hidden="true" />
        <AutoComplete
          inputId="skillSearchInput"
          value={searchQuery}
          suggestions={skillSuggestions}
          completeMethod={() => undefined}
          field="name"
          itemTemplate={(skill: Skill) => (
            <span className="nc_skill-suggestion">
              <span className="nc_skill-suggestion__code">{skill.code}</span>: {skill.name}
            </span>
          )}
          placeholder="Search for any skill by its name"
          aria-label="Search for any skill by its name"
          onChange={(event: AutoCompleteChangeEvent) => {
            if (typeof event.value === 'string') onSearchChange(event.value);
          }}
          onSelect={event => onSkillSelect(event.value as Skill)}
        />
        {isSearching && (
          <button type="button" aria-label="Clear search" onClick={() => onSearchChange('')}>
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="nc_report-settings__fields">
        <label className="nc_report-settings__field">
          <span className="nc_report-settings__label">
            Roadmap <abbr title="required">*</abbr>
          </span>
          <AutoComplete
            value={form.roadmap}
            suggestions={filteredRoadmaps}
            completeMethod={event => setFilteredRoadmaps(filterBy(event, optionsList.roadmap))}
            field="label"
            dropdown
            placeholder="Select a Roadmap"
            onChange={(event: AutoCompleteChangeEvent) => handleRoadmapChange(event.value)}
          />
        </label>

        <label className="nc_report-settings__field">
          <span className="nc_report-settings__label">
            Unit <abbr title="required">*</abbr>
          </span>
          <AutoComplete
            value={form.unit}
            suggestions={filteredUnits}
            completeMethod={event => setFilteredUnits(filterBy(event, availablesOptions.unit))}
            field="label"
            dropdown
            disabled={!form.unitEnabled}
            placeholder={form.unitEnabled ? 'Select a Unit' : 'Start by choosing a Roadmap'}
            onChange={(event: AutoCompleteChangeEvent) => handleUnitChange(event.value)}
          />
        </label>
      </div>
    </div>
  );
};

export default UnitsReportSettings;
